import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '@/components/FormElement.tsx';
import { InputText } from '@/components/Inputs/InputText.tsx';
import Button from '@/components/core/Button.tsx';
import { IconPlus } from '@/icons';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { toast } from '@/hooks/useToast';
import {
  DepartmentAccessLevelDTO,
  useGetUsersForDepartment,
  useInviteUserToDepartment,
  getGetUsersForDepartmentQueryKey,
} from '@/api/trafikportalen';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AccessLevelDescription,
  AccessLevelToggle,
} from '../Administrators/AccessLevelToggle';
import { getListAvailableUserProfilesQueryKey } from '@/api/auth';
import { refreshDepartmentAccess } from '@/stores/auth';
import { trimData } from '@/helpers/trimData';

interface AddDepartmentAdminDialogProps {
  departmentId: string;
  onClose: () => void;
}

const inviteUserToDepartmentBody = z.object({
  email: z.string().email(),
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  department_id: z.string(),
  access_level: z.enum(['viewer', 'bearer-manager', 'editor']),
});

type AddDepartmentAdminType = z.infer<typeof inviteUserToDepartmentBody>;

export const AddDepartmentAdminDialog = ({
  departmentId,
  onClose,
}: AddDepartmentAdminDialogProps) => {
  const { formatMessage } = useIntl();
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  const inviteUserToDepartment = useInviteUserToDepartment();
  const queryClient = useQueryClient();
  const { data: departmentAdmins, isLoading } =
    useGetUsersForDepartment(departmentId);

  const methods = useForm<AddDepartmentAdminType>({
    defaultValues: {
      email: '',
      firstname: '',
      lastname: '',
      department_id: departmentId,
      access_level: DepartmentAccessLevelDTO.editor,
    },
    resolver: zodResolver(inviteUserToDepartmentBody, {
      errorMap: customErrorMap(),
    }),
    mode: 'all',
  });

  const watchEmail = methods.watch('email');
  const watchAccessLevel = methods.watch('access_level');

  const isEmailAlreadyInUse = useMemo(() => {
    return departmentAdmins?.some(
      (admin) => admin.email?.toLowerCase() === watchEmail?.toLowerCase(),
    );
  }, [departmentAdmins, watchEmail]);

  const onSubmit: SubmitHandler<AddDepartmentAdminType> = useCallback(
    async (data) => {
      const submitData = trimData({ ...data, email: data.email.toLowerCase() });
      try {
        await inviteUserToDepartment.mutateAsync({
          data: {
            ...submitData,
            department_id: departmentId,
          },
        });

        onClose();
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetUsersForDepartmentQueryKey(departmentId),
          }),
          queryClient.invalidateQueries({
            queryKey: getListAvailableUserProfilesQueryKey(),
          }),
        ]);

        await refreshDepartmentAccess();

        toast.success(
          {
            title: `Administratören ${data.firstname} ${data.lastname} lades till`,
          },
          { autoClose: 3000 },
        );
      } catch (error) {
        console.error('Failed to add department administrator:', error);
        toast.error(
          {
            title: 'Kunde inte lägga till administratören',
          },
          { autoClose: 3000 },
        );
      }
    },
    [inviteUserToDepartment, departmentId, onClose, queryClient],
  );

  const isSubmitDisabled = useMemo(() => {
    if (inviteUserToDepartment.isPending) {
      return true;
    }

    if (isEmailAlreadyInUse) {
      return true;
    }

    if (isLoading) {
      return true;
    }

    if (!methods.formState.isDirty) {
      return true;
    }

    if (!methods.formState.isValid) {
      return true;
    }

    return false;
  }, [
    inviteUserToDepartment.isPending,
    isEmailAlreadyInUse,
    isLoading,
    methods.formState.isDirty,
    methods.formState.isValid,
  ]);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex flex-col gap-2"
      >
        <FormElement
          label="E-postadress"
          required={true}
          input={
            <InputText
              name="email"
              control={methods.control}
              editMode={true}
              error={
                isEmailAlreadyInUse
                  ? {
                      message: formatMessage({
                        defaultMessage:
                          'En administratör med denna e-postadress finns redan i avdelningen',
                      }),
                      type: 'custom',
                    }
                  : methods.formState.errors.email
              }
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          label="Förnamn"
          required={true}
          input={
            <InputText
              name="firstname"
              control={methods.control}
              editMode={true}
              error={methods.formState.errors.firstname}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          label="Efternamn"
          required={true}
          input={
            <InputText
              name="lastname"
              control={methods.control}
              editMode={true}
              error={methods.formState.errors.lastname}
              helperTextReserveSpace
            />
          }
        />
        <FormElement
          label={
            <span id="department-access-level-label">
              <FormattedMessage defaultMessage="Rättighet" />
            </span>
          }
          input={
            <div className="flex flex-col gap-2">
              <AccessLevelToggle
                id="department_access_level"
                labelledBy="department-access-level-label"
                value={watchAccessLevel}
                onChange={(level) => {
                  if (level !== DepartmentAccessLevelDTO.none) {
                    methods.setValue('access_level', level);
                  }
                }}
              />
              <p className="text-text-700">
                <AccessLevelDescription level={watchAccessLevel} />
              </p>
            </div>
          }
        />
        <i className="text-text-800">
          <FormattedMessage defaultMessage="Detta sätter bara den initiala informationen. Efter detta är det enbart användaren själv som kan redigera sin information." />
        </i>
        <Button
          startIcon={<IconPlus />}
          variant="filled"
          type="submit"
          className="w-full"
          disabled={isSubmitDisabled}
          loading={inviteUserToDepartment.isPending}
        >
          <FormattedMessage defaultMessage="Lägg till administratör" />
        </Button>
      </form>
    </FormProvider>
  );
};
